import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./ProfileEditModalNew.module.scss";
import { motion, AnimatePresence } from "framer-motion";
import { connect } from "react-redux";
import { useFormik } from "formik";
import SkillsEdit from "./SkillsEdit/SkillsEdit";
import ProfileConnectSelector from "./ProfileConnectSelector/ProfileConnectSelector.tsx";
import {
    getCities,
    getUserSkill,
    updateUserProfileNew
} from "../../../store/actions";
import JurisdictionList from "../../UI/JurisdictionList/JurisdictionList";
import * as Yup from "yup";
import useUserImage from "../../../hooks/useUserImage";
import ErrorMenu from "./ErrorMenu";
import useIsPhone from "../../../hooks/useIsPhone";
import { X, Camera, Link as LinkIcon, UserRound, HardHat, Building2, MapPin, Trash2, Info, ChevronDown, ChevronLeft, ChevronRight, Search, Globe, Users, LocateFixed, Check } from 'lucide-react';
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import {
    buildInitialProfileJobBoardCoverage,
    DEFAULT_INFLUENCE_RADIUS_KM,
    isProfileJobBoardSaveReady,
    profileJobBoardApplyAllCountryMode,
    profileJobBoardApplyListedZonesMode,
    profileJobBoardApplyMapRadiusMode,
} from "../../../utils/workCoverage/jobBoardCoverageModel.ts";
import {
    COMMERCE_DELIVERY_RADIUS_KM_MIN,
    COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
} from "../../../services/commerceService.ts";
import CommerceDeliveryMap from "../../OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceDeliveryMap.tsx";
import commerceFormStyles from "../../OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.module.scss";

const ZONE_INFLUENCE_PRESETS = [
    { label: 'Muy cerca', km: 5 },
    { label: 'Barrio', km: 10 },
    { label: 'Ciudad chica', km: 25 },
    { label: 'Ciudad', km: 50 },
    { label: 'Zona amplia', km: 100 },
];

function clampRadiusKm(km) {
    return Math.min(
        COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
        Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, Math.round(km))
    );
}

function cloneListedJobBoardZones(zones) {
    if (!Array.isArray(zones)) return [];
    return zones
        .filter((z) => z && z.allCountry !== true)
        .map((z) => ({
            ...z,
            jurisdiccion: z.jurisdiccion && typeof z.jurisdiccion === "object" ? { ...z.jurisdiccion } : z.jurisdiccion,
        }));
}

function createEmptyCoverageModeDraft() {
    return {
        specificZones: { jobBoardZones: [] },
        mapRadius: {
            influenceLat: null,
            influenceLng: null,
            influenceRadiusKm: DEFAULT_INFLUENCE_RADIUS_KM,
        },
    };
}

const variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
};


const ProfileEditModalNew = ({
    setIsEditing,
    userData,
    getUserSkill,
    userSkills,
    cities,
    getCities,
    updateUserProfileNew,
    ...props }) => {

    const [activeSection, setActiveSection] = useState('Datos personales');
    const { imageError, initials, profileImage } = useUserImage(userData);
    const [imagePreview, setImagePreview] = useState(null);
    const [editImg, setEditImg] = useState(false);
    const [file, setFile] = useState(null);
    const [editSkills, setEditSkills] = useState(false);
    const [selectedJurisdiction, setSelectedJurisdiction] = useState({ id: "", name: "" });
    const [showErrors, setShowErrors] = useState(false);
    const [showLeftSide, setShowLeftSide] = useState(true);
    const [citySearchFilter, setCitySearchFilter] = useState("");

    const isPhone = useIsPhone();

    const userFullName = userData.type === "COMPANY" ? userData.razon_social : userData.name + " " + userData.last_name;

    const inputFileRef = useRef(null);
    const initialUserSkillsRef = useRef(userSkills);
    const initialImageRef = useRef(userData?.image_profile || "");
    const initialSkillsSetRef = useRef(false);
    const initialImageSetRef = useRef(false);

    const handleEditImgClick = () => {
        if (inputFileRef.current) {
            inputFileRef.current.click();
        }

        setEditImg(!editImg);
    };

    useEffect(() => {
        getUserSkill(userData.id);
    }, []);

    const addHttpToUrl = (url) => {
        if (!/^https?:\/\//i.test(url)) {
            return `http://${url}`;
        }
        return url;
    };

    const urlRegex = /^(https?:\/\/)?(www\.)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;
    const twitterRegex = /^(https?:\/\/)?(www\.)?twitter\.com\/.+$/i;
    const facebookRegex = /^(https?:\/\/)?(www\.)?facebook\.com\/.+$/i;
    const instagramRegex = /^(https?:\/\/)?(www\.)?instagram\.com\/.+$/i;
    const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/i;
    const requiredFieldSchema = (label, max = 50) =>
        Yup.string()
            .required(`Debes ingresar un ${label}`)
            .notOneOf(["Agregar"], "Coloque un nombre válido")
            .max(max, `Máximo ${max} caracteres`);
    const urlFieldSchema = (regex, message) =>
        Yup.string()
            .transform(addHttpToUrl)
            .matches(regex, message);
    const handleRegex = /^@?[a-zA-Z0-9._-]{2,50}$/;
    const normalizeSocialInput = (value) => {
        if (!value) return value;
        if (value.startsWith("@")) return value;
        if (value.includes("://")) return value;
        if (value.includes(".") || value.includes("/")) return `http://${value}`;
        return value;
    };
    const socialFieldSchema = (label) =>
        Yup.string().test(
            "social",
            `Ingrese un ${label} válido`,
            (value) => {
                if (!value) return true;
                const normalized = normalizeSocialInput(value.trim());
                return urlRegex.test(normalized) || handleRegex.test(value.trim());
            }
        );

    const jobBoardInitial = useMemo(
        () => buildInitialProfileJobBoardCoverage(userData),
        [
            userData?.jobBoardZones,
            userData?.influenceGeo,
            userData?.latitude,
            userData?.longitude,
            userData?.influenceRadiusKm,
            userData?.id,
        ]
    );

    const [mapTabCycleKey, setMapTabCycleKey] = useState(0);
    const prevWorkCoverageKindRef = useRef(null);
    const coverageModeDraftRef = useRef(createEmptyCoverageModeDraft());

    const formik = useFormik({
        initialValues: {
            name: userData?.name === undefined || userData.name === null ? "" : userData.name,
            last_name: userData?.last_name === undefined || userData.last_name === null ? "" : userData.last_name,
            razon_social: userData?.razon_social === undefined || userData.razon_social === null ? "" : userData.razon_social,
            type: userData?.type,
            jobBoardZones: jobBoardInitial.jobBoardZones,
            allCountry: jobBoardInitial.allCountry,
            workCoverageKind: jobBoardInitial.workCoverageKind,
            influenceLat: jobBoardInitial.influenceLat,
            influenceLng: jobBoardInitial.influenceLng,
            influenceRadiusKm: jobBoardInitial.influenceRadiusKm,
            phone: userData.phone === undefined || userData.phone === null ? "" : userData.phone,
            url_site_web: userData.url_site_web === undefined || userData.url_site_web === null ? "" : userData.url_site_web,
            instagram: userData.instagram === undefined || userData.instagram === null ? "" : userData.instagram,
            facebook: userData.facebook === undefined || userData.facebook === null ? "" : userData.facebook,
            twitter: userData.twitter === undefined || userData.twitter === null ? "" : userData.twitter,
            linkedin: userData.linkedin === undefined || userData.linkedin === null ? "" : userData.linkedin,
            description: userData.description === undefined || userData.description === null || userData.description === "" ? "" : userData.description,
            image: userData.image_profile === undefined || userData.image_profile === null ? "" : userData.image_profile,
            userSkills: userSkills,
            categoryConnect: (() => {
                const cc = userData?.categoryConnect;
                if (Array.isArray(cc)) {
                    return cc.map((c) => Number(c?.id ?? c)).filter(Number.isFinite);
                }
                return [];
            })()
        },
        validationSchema: Yup.object({
            razon_social: Yup.string().when("type", {
                is: "COMPANY",
                then: requiredFieldSchema("nombre de empresa"),
            }),
            name: Yup.string().when("type", {
                is: "PERSON",
                then: requiredFieldSchema("nombre"),
            }),
            last_name: Yup.string().when("type", {
                is: "PERSON",
                then: requiredFieldSchema("apellido"),
            }),
            userSkills: Yup.array().min(1, "Debes seleccionar al menos un rubro"),
            url_site_web: urlFieldSchema(urlRegex, "Ingrese una URL válida (ej: www.mi_sitio_web.com)"),
            twitter: socialFieldSchema("Twitter").nullable(),
            facebook: socialFieldSchema("Facebook").nullable(),
            instagram: socialFieldSchema("Instagram").nullable(),
            linkedin: socialFieldSchema("LinkedIn").nullable(),
        }),
        validateOnBlur: true,
        enableReinitialize: true,
    });

    const persistCurrentCoverageModeToDraft = () => {
        const k = formik.values.workCoverageKind;
        const refDraft = coverageModeDraftRef.current;
        if (k === 'specificZones') {
            refDraft.specificZones.jobBoardZones = cloneListedJobBoardZones(formik.values.jobBoardZones);
        } else if (k === 'mapRadius') {
            refDraft.mapRadius.influenceLat = formik.values.influenceLat;
            refDraft.mapRadius.influenceLng = formik.values.influenceLng;
            refDraft.mapRadius.influenceRadiusKm = formik.values.influenceRadiusKm;
        }
    };

    useEffect(() => {
        coverageModeDraftRef.current = {
            specificZones: { jobBoardZones: cloneListedJobBoardZones(jobBoardInitial.jobBoardZones) },
            mapRadius: {
                influenceLat: jobBoardInitial.influenceLat,
                influenceLng: jobBoardInitial.influenceLng,
                influenceRadiusKm: jobBoardInitial.influenceRadiusKm,
            },
        };
    }, [jobBoardInitial]);

    useEffect(() => {
        const prev = prevWorkCoverageKindRef.current;
        if (formik.values.workCoverageKind === 'mapRadius' && prev !== 'mapRadius') {
            setMapTabCycleKey((k) => k + 1);
        }
        prevWorkCoverageKindRef.current = formik.values.workCoverageKind;
    }, [formik.values.workCoverageKind]);

    useEffect(() => {
        formik.setFieldValue("userSkills", userSkills);
    }, [userSkills]);


    useEffect(() => {
        if (selectedJurisdiction?.id) {
            getCities(selectedJurisdiction?.id);
            setCitySearchFilter("");
        }
    }, [selectedJurisdiction?.id]);

    useEffect(() => {
        if (!initialSkillsSetRef.current && Array.isArray(userSkills)) {
            initialUserSkillsRef.current = userSkills;
            initialSkillsSetRef.current = true;
        }
        if (!initialImageSetRef.current) {
            initialImageRef.current = userData?.image_profile || "";
            initialImageSetRef.current = true;
        }
    }, [userSkills, userData?.image_profile]);

    const handleSectionChange = (section) => {
        setActiveSection(section);
        if (isPhone) {
            setShowLeftSide(false);
        }
    };

    // Función para volver al menú en móviles
    const handleBackToMenu = () => {
        setShowLeftSide(true);
    };

    const renderContent = () => {
        switch (activeSection) {
            case 'Datos personales':
                return (
                    <form onSubmit={formik.handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="type">Tipo de cuenta</label>
                            <div className={styles.formGroup_buttons}>
                                <button
                                    className={`${styles.formGroup_buttons_button} ${formik.values.type === "PERSON" ? styles.formGroup_buttons_button_active : ""}`}
                                    type="button"
                                    onClick={() => {
                                        formik.setFieldValue("type", "PERSON");
                                        formik.setFieldTouched("name", true, false);
                                        formik.setFieldTouched("last_name", true, false);
                                    }}
                                >
                                    <UserRound size={20} />
                                    <span>Persona</span>
                                </button>
                                <button
                                    className={`${styles.formGroup_buttons_button} ${formik.values.type === "COMPANY" ? styles.formGroup_buttons_button_active : ""}`}
                                    type="button"
                                    onClick={() => {
                                        formik.setFieldValue("type", "COMPANY");
                                        formik.setFieldTouched("razon_social", true, false);
                                    }}
                                >
                                    <Building2 size={20} />
                                    <span>Empresa</span>
                                </button>
                            </div>
                        </div>
                        {formik.values.type === "COMPANY" ? (
                            <div className={styles.formGroup}>
                                <label className={styles.formGroup_label} htmlFor="razon_social">Razón social</label>
                                <input
                                    className={`${styles.formGroup_input} ${formik.errors.razon_social && styles.error}`}
                                    type="text"
                                    name="razon_social"
                                    id="razon_social"
                                    placeholder="Nombre de la empresa"
                                    value={formik.values.razon_social}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                            </div>
                        ) : (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={styles.formGroup_label} htmlFor="name">Nombre</label>
                                    <input
                                        className={`${styles.formGroup_input} ${formik.errors.name && styles.error}`}
                                        type="text"
                                        name="name"
                                        placeholder="Nombre"
                                        id="name"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formGroup_label} htmlFor="last_name">Apellido</label>
                                    <input
                                        className={`${styles.formGroup_input} ${formik.errors.last_name && styles.error}`}
                                        type="text"
                                        name="last_name"
                                        id="last_name"
                                        placeholder="Apellido"
                                        value={formik.values.last_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                            </>
                        )}
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="phone">Teléfono</label>
                            <input
                                className={`${styles.formGroup_input} ${formik.errors.phone && styles.error}`}
                                type="text"
                                name="phone"
                                id="phone"
                                placeholder="Teléfono"
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="description">Descripción</label>
                            <textarea
                                name="description"
                                id="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder={formik.values.type === "COMPANY" ? "Describe tu empresa . . ." : "Describe tu perfil . . ."}
                                maxLength={500}
                                className={styles.formGroup_textArea}
                            />
                            <div className={styles.formGroup_textArea_counter}>Máximo {formik.values.description.length}/500 caracteres</div>
                        </div>
                    </form>
                );
            case 'Zona de trabajo':
                return (
                    <div className={styles.profileModal_form_container}>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="jobBoardZones">
                                <MapPin size={16} />
                                Zona de trabajo
                            </label>

                            <div className={styles.formGroup_workZoneOptions} role="radiogroup" aria-label="Tipo de cobertura de trabajo">
                                <button
                                    type="button"
                                    className={`${styles.formGroup_workZoneOptions_option} ${formik.values.workCoverageKind === 'allCountry' ? styles.formGroup_workZoneOptions_option_active : ""}`}
                                    onClick={() => {
                                        persistCurrentCoverageModeToDraft();
                                        const p = profileJobBoardApplyAllCountryMode();
                                        Object.keys(p).forEach((key) => formik.setFieldValue(key, p[key]));
                                        setSelectedJurisdiction({ id: "", name: "" });
                                        setCitySearchFilter("");
                                    }}
                                >
                                    <span
                                        className={`${styles.formGroup_workZoneOptions_optionIcon} ${
                                            formik.values.workCoverageKind === 'allCountry'
                                                ? styles.formGroup_workZoneOptions_optionIconActive
                                                : ''
                                        }`}
                                        aria-hidden
                                    >
                                        <Globe size={22} strokeWidth={1.75} />
                                    </span>
                                    <span className={styles.formGroup_workZoneOptions_optionMain}>
                                        <span className={styles.formGroup_workZoneOptions_optionTitle}>Todo el país</span>
                                        <span className={styles.formGroup_workZoneOptions_optionHint}>Cobertura en toda Argentina</span>
                                    </span>
                                    {formik.values.workCoverageKind === 'allCountry' ? (
                                        <Check size={20} className={styles.formGroup_workZoneOptions_optionCheck} aria-hidden />
                                    ) : (
                                        <span className={styles.formGroup_workZoneOptions_optionCheckSpacer} aria-hidden />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.formGroup_workZoneOptions_option} ${formik.values.workCoverageKind === 'specificZones' ? styles.formGroup_workZoneOptions_option_active : ""}`}
                                    onClick={() => {
                                        persistCurrentCoverageModeToDraft();
                                        const p = profileJobBoardApplyListedZonesMode();
                                        Object.keys(p).forEach((key) => formik.setFieldValue(key, p[key]));
                                        formik.setFieldValue(
                                            "jobBoardZones",
                                            cloneListedJobBoardZones(coverageModeDraftRef.current.specificZones.jobBoardZones)
                                        );
                                    }}
                                >
                                    <span
                                        className={`${styles.formGroup_workZoneOptions_optionIcon} ${
                                            formik.values.workCoverageKind === 'specificZones'
                                                ? styles.formGroup_workZoneOptions_optionIconActive
                                                : ''
                                        }`}
                                        aria-hidden
                                    >
                                        <MapPin size={22} strokeWidth={1.75} />
                                    </span>
                                    <span className={styles.formGroup_workZoneOptions_optionMain}>
                                        <span className={styles.formGroup_workZoneOptions_optionTitle}>Provincias y ciudades</span>
                                        <span className={styles.formGroup_workZoneOptions_optionHint}>Elegí una o más zonas del listado</span>
                                    </span>
                                    {formik.values.workCoverageKind === 'specificZones' ? (
                                        <Check size={20} className={styles.formGroup_workZoneOptions_optionCheck} aria-hidden />
                                    ) : (
                                        <span className={styles.formGroup_workZoneOptions_optionCheckSpacer} aria-hidden />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.formGroup_workZoneOptions_option} ${formik.values.workCoverageKind === 'mapRadius' ? styles.formGroup_workZoneOptions_option_active : ""}`}
                                    onClick={() => {
                                        persistCurrentCoverageModeToDraft();
                                        const d = coverageModeDraftRef.current.mapRadius;
                                        const restoreCoords =
                                            d.influenceLat != null &&
                                            d.influenceLng != null &&
                                            Number.isFinite(d.influenceLat) &&
                                            Number.isFinite(d.influenceLng)
                                                ? { influenceLat: d.influenceLat, influenceLng: d.influenceLng }
                                                : null;
                                        const radiusKm = Number.isFinite(Number(d.influenceRadiusKm))
                                            ? d.influenceRadiusKm
                                            : formik.values.influenceRadiusKm;
                                        const p = profileJobBoardApplyMapRadiusMode(radiusKm, restoreCoords);
                                        Object.keys(p).forEach((key) => formik.setFieldValue(key, p[key]));
                                        setSelectedJurisdiction({ id: "", name: "" });
                                        setCitySearchFilter("");
                                    }}
                                >
                                    <span
                                        className={`${styles.formGroup_workZoneOptions_optionIcon} ${
                                            formik.values.workCoverageKind === 'mapRadius'
                                                ? styles.formGroup_workZoneOptions_optionIconActive
                                                : ''
                                        }`}
                                        aria-hidden
                                    >
                                        <LocateFixed size={22} strokeWidth={1.75} />
                                    </span>
                                    <span className={styles.formGroup_workZoneOptions_optionMain}>
                                        <span className={styles.formGroup_workZoneOptions_optionTitle}>Zona en mapa</span>
                                        <span className={styles.formGroup_workZoneOptions_optionHint}>Centro y radio en kilómetros</span>
                                    </span>
                                    {formik.values.workCoverageKind === 'mapRadius' ? (
                                        <Check size={20} className={styles.formGroup_workZoneOptions_optionCheck} aria-hidden />
                                    ) : (
                                        <span className={styles.formGroup_workZoneOptions_optionCheckSpacer} aria-hidden />
                                    )}
                                </button>
                            </div>

                            {formik.values.workCoverageKind === 'allCountry' && (
                                <div className={styles.formGroup_workZone_message}>
                                    <Info size={16} />
                                    <span>Trabajas o vendes tus productos y/o servicios en todo el país.</span>
                                </div>
                            )}

                            {formik.values.workCoverageKind === 'specificZones' &&
                                Array.isArray(formik.values.jobBoardZones) &&
                                formik.values.jobBoardZones.filter(zone => zone.allCountry !== true).length > 0 && (
                                    <div className={styles.formGroup_workZone_selectedZones}>
                                        <div className={styles.formGroup_workZone_title}>Zonas seleccionadas</div>
                                        {formik.values.jobBoardZones.filter(zone => zone.allCountry !== true).map((zone, index) => (
                                            <div key={index} className={styles.formGroup_workZone_citySelected}>
                                                <div className={styles.formGroup_workZone_citySelected_info}>
                                                    <span className={styles.formGroup_workZone_citySelected_info_province}>{zone?.jurisdiccion?.name || zone?.name}</span>
                                                    <span className={styles.formGroup_workZone_citySelected_info_city}>{zone?.city?.split(",")[0]}</span>
                                                </div>
                                                <button
                                                    className={styles.formGroup_workZone_citySelected_button}
                                                    type="button"
                                                    onClick={() => {
                                                        const listed = formik.values.jobBoardZones.filter((z) => z.allCountry !== true);
                                                        const target = listed[index];
                                                        const jid = target?.jurisdiccion?.id ?? target?.jurisdictionId ?? target?.jurisdiccion;
                                                        const cityName = target?.city || "";
                                                        const newZones = formik.values.jobBoardZones.filter(
                                                            (z) =>
                                                                z.allCountry === true ||
                                                                String(z.jurisdiccion?.id ?? z.jurisdictionId ?? z.jurisdiccion) !== String(jid) ||
                                                                (z.city || "") !== cityName
                                                        );
                                                        formik.setFieldValue("jobBoardZones", newZones);
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                            {formik.values.workCoverageKind === 'specificZones' && (
                                <motion.div
                                    className={styles.formGroup_workZone}
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className={styles.formGroup_workZone_message}>
                                        <Info size={16} />
                                        <span>Selecciona una o más provincias y ciudades donde trabajas</span>
                                    </div>
                                    <div className={styles.formGroup_workZone_title}>Provincia</div>
                                    <select
                                        className={styles.formGroup_workZone_select}
                                        name="jurisdictionList"
                                        id="jurisdictionList"
                                        value={selectedJurisdiction?.id || ""}
                                        onChange={(e) => {
                                            const name = e.target.options[e.target.selectedIndex].dataset.name;
                                            setSelectedJurisdiction({
                                                id: e.target.value,
                                                name: name || ""
                                            });
                                        }}
                                    >
                                        <option value="">Seleccionar provincia</option>
                                        {JurisdictionList.map((jurisdiction, index) => (
                                            <option key={index} value={jurisdiction.id} data-name={jurisdiction.name}>
                                                {jurisdiction.name}
                                            </option>
                                        ))}
                                    </select>
                                    {cities.length > 0 && selectedJurisdiction.name !== "" && (
                                        <div className={styles.formGroup_workZone_cityList}>
                                            <div className={styles.formGroup_workZone_title}>Ciudades</div>
                                            <div className={styles.formGroup_workZone_cityList_search}>
                                                <Search size={16} className={styles.formGroup_workZone_cityList_search_icon} />
                                                <input
                                                    type="text"
                                                    placeholder="Buscar ciudad..."
                                                    value={citySearchFilter}
                                                    onChange={(e) => setCitySearchFilter(e.target.value)}
                                                    className={styles.formGroup_workZone_cityList_search_input}
                                                />
                                            </div>
                                            {cities.filter((city) => {
                                                if (!citySearchFilter) return true;
                                                return city.name.toLowerCase().includes(citySearchFilter.toLowerCase());
                                            }).length > 0 ? (
                                                <div className={styles.formGroup_workZone_cityList_container}>
                                                    {cities
                                                        .filter((city) => {
                                                            if (!citySearchFilter) return true;
                                                            return city.name.toLowerCase().includes(citySearchFilter.toLowerCase());
                                                        })
                                                        .map((city, index) => {
                                                            const isChecked = formik.values.jobBoardZones.some(zone => zone.city === city.name);

                                                            return (
                                                                <label key={index} className={styles.formGroup_workZone_cityList_item}>
                                                                    <input
                                                                        className={styles.formGroup_workZone_cityList_item_checkbox}
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            const lm = profileJobBoardApplyListedZonesMode();
                                                                            formik.setFieldValue("workCoverageKind", lm.workCoverageKind);
                                                                            formik.setFieldValue("allCountry", lm.allCountry);
                                                                            formik.setFieldValue("influenceLat", lm.influenceLat);
                                                                            formik.setFieldValue("influenceLng", lm.influenceLng);
                                                                            if (e.target.checked) {
                                                                                const newZones = [
                                                                                    ...formik.values.jobBoardZones,
                                                                                    {
                                                                                        jurisdiccion: selectedJurisdiction?.id,
                                                                                        name: selectedJurisdiction?.name,
                                                                                        city: city.name
                                                                                    }
                                                                                ];
                                                                                formik.setFieldValue("jobBoardZones", newZones);
                                                                            } else {
                                                                                const updatedZones = formik.values.jobBoardZones.filter(zone => zone.city !== city.name);
                                                                                formik.setFieldValue("jobBoardZones", updatedZones);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <p style={{ color: "rgb(55 65 81)" }}>{city?.name?.split(",")[0]}</p>
                                                                </label>
                                                            );
                                                        })}
                                                </div>
                                            ) : (
                                                <div className={styles.formGroup_workZone_message}>
                                                    <Info size={16} />
                                                    <span>No se encontraron ciudades con "{citySearchFilter}"</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {formik.values.workCoverageKind === 'mapRadius' && (
                                <div className={styles.formGroup_workZoneMapStack}>
                                    <div className={commerceFormStyles.deliveryMapColMap}>
                                        <CommerceDeliveryMap
                                            key={mapTabCycleKey}
                                            lat={formik.values.influenceLat}
                                            lng={formik.values.influenceLng}
                                            radiusKm={Number.isFinite(formik.values.influenceRadiusKm) ? formik.values.influenceRadiusKm : 50}
                                            onPositionChange={(lat, lng) => {
                                                formik.setFieldValue("influenceLat", lat);
                                                formik.setFieldValue("influenceLng", lng);
                                            }}
                                        />
                                    </div>
                                    <aside className={commerceFormStyles.deliveryMapColPanel}>
                                        <p className={commerceFormStyles.deliveryPanelTitle}>Centro del mapa</p>
                                        <p className={commerceFormStyles.mapHint}>
                                            Mové el pin o tocá el mapa para marcar dónde trabajás; ajustá el radio en km.
                                        </p>
                                        <p className={commerceFormStyles.influenceSubheading} id="profile-influence-presets-heading">
                                            Tamaño de la zona de influencia
                                        </p>
                                        <p className={commerceFormStyles.zoneHint}>
                                            Los botones van hasta ~100 km; para más alcance usá el deslizador o el número (hasta{' '}
                                            {COMMERCE_DELIVERY_RADIUS_UI_KM_MAX} km).
                                        </p>
                                        <div
                                            className={commerceFormStyles.radiusChips}
                                            role="group"
                                            aria-labelledby="profile-influence-presets-heading"
                                        >
                                            {ZONE_INFLUENCE_PRESETS.map(({ label, km }) => (
                                                <button
                                                    key={km}
                                                    type="button"
                                                    className={`${commerceFormStyles.radiusChip} ${
                                                        formik.values.influenceRadiusKm === km ? commerceFormStyles.radiusChipActive : ''
                                                    }`}
                                                    onClick={() => formik.setFieldValue('influenceRadiusKm', km)}
                                                >
                                                    <span className={commerceFormStyles.radiusChipTitle}>{label}</span>
                                                    <span className={commerceFormStyles.radiusChipKm}>~{km} km</span>
                                                </button>
                                            ))}
                                        </div>
                                        <div className={commerceFormStyles.radiusRow}>
                                            <div className={commerceFormStyles.radiusLabelRow}>
                                                <label className={commerceFormStyles.radiusLabel} htmlFor="profile-radius-km">
                                                    Afinar alcance (km)
                                                </label>
                                                <div className={commerceFormStyles.radiusValueRow}>
                                                    <input
                                                        id="profile-radius-km-num"
                                                        type="number"
                                                        className={commerceFormStyles.radiusNumber}
                                                        min={COMMERCE_DELIVERY_RADIUS_KM_MIN}
                                                        max={COMMERCE_DELIVERY_RADIUS_UI_KM_MAX}
                                                        step={1}
                                                        value={formik.values.influenceRadiusKm}
                                                        onChange={(e) => {
                                                            const v = e.target.valueAsNumber;
                                                            if (!Number.isFinite(v)) return;
                                                            formik.setFieldValue('influenceRadiusKm', clampRadiusKm(v));
                                                        }}
                                                        aria-label="Kilómetros de alcance de la zona de influencia"
                                                    />
                                                    <span className={commerceFormStyles.radiusValue}>km</span>
                                                </div>
                                            </div>
                                            <input
                                                id="profile-radius-km"
                                                type="range"
                                                className={commerceFormStyles.radiusSlider}
                                                min={COMMERCE_DELIVERY_RADIUS_KM_MIN}
                                                max={COMMERCE_DELIVERY_RADIUS_UI_KM_MAX}
                                                step={1}
                                                value={formik.values.influenceRadiusKm}
                                                onChange={(e) => {
                                                    const v = Number(e.target.value);
                                                    if (!Number.isFinite(v)) return;
                                                    formik.setFieldValue('influenceRadiusKm', clampRadiusKm(v));
                                                }}
                                            />
                                        </div>
                                    </aside>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'Mis rubros':
                return (
                    <div className={styles.profileModal_form_container}>
                        <SkillsEdit
                            editSkills={editSkills}
                            setEditSkills={setEditSkills}
                            userSkills={userSkills}
                            setFieldValue={formik.setFieldValue} />
                    </div>

                )
            case 'Conexiones':
                return (
                    <div className={styles.profileModal_form_container}>
                        <ProfileConnectSelector
                            selectedCategories={formik.values.categoryConnect}
                            onChange={(categories) => formik.setFieldValue('categoryConnect', categories)}
                        />
                    </div>
                )
            case 'Redes':
                return (
                    <form onSubmit={formik.handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="url_site_web">Sitio web</label>
                            <input
                                type="text"
                                name="url_site_web"
                                id="url_site_web"
                                placeholder="https://mi_sitio_web.com"
                                className={`${styles.formGroup_input} ${formik.errors.url_site_web && styles.error}`}
                                value={formik.values.url_site_web}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="facebook">Facebook</label>
                            <input
                                type="text"
                                name="facebook"
                                id="facebook"
                                placeholder="https://facebook.com/mi_cuenta"
                                className={`${styles.formGroup_input} ${formik.errors.facebook && styles.error}`}
                                value={formik.values.facebook}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="instagram">Instagram</label>
                            <input
                                type="text"
                                name="instagram"
                                id="instagram"
                                placeholder="https://instagram.com/mi_cuenta"
                                className={`${styles.formGroup_input} ${formik.errors.instagram && styles.error}`}
                                value={formik.values.instagram}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formGroup_label} htmlFor="linkedin">LinkedIn</label>
                            <input
                                type="text"
                                name="linkedin"
                                id="linkedin"
                                placeholder="https://linkedin.com/mi_cuenta"
                                className={`${styles.formGroup_input} ${formik.errors.linkedin && styles.error}`}
                                value={formik.values.linkedin}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </div>
                    </form>
                );
            default:
                return null;
        }
    }


    const hasChanges = useMemo(() => {
        const currentValues = formik.values;
        const initialValues = formik.initialValues;
        const initialSkills = initialUserSkillsRef.current || [];
        const currentSkills = userSkills || [];

        const normalizeString = (str) => (str || "").trim();
        const normalizeUrl = (url) => {
            if (!url) return "";
            return url.trim().replace(/^https?:\/\//, "").replace(/^www\./, "").toLowerCase();
        };

        const hasFormChanges = 
            normalizeString(currentValues.name) !== normalizeString(initialValues.name) ||
            normalizeString(currentValues.last_name) !== normalizeString(initialValues.last_name) ||
            normalizeString(currentValues.razon_social) !== normalizeString(initialValues.razon_social) ||
            currentValues.type !== initialValues.type ||
            normalizeString(currentValues.phone) !== normalizeString(initialValues.phone) ||
            normalizeString(currentValues.description) !== normalizeString(initialValues.description) ||
            normalizeUrl(currentValues.url_site_web) !== normalizeUrl(initialValues.url_site_web) ||
            normalizeUrl(currentValues.instagram) !== normalizeUrl(initialValues.instagram) ||
            normalizeUrl(currentValues.facebook) !== normalizeUrl(initialValues.facebook) ||
            normalizeUrl(currentValues.twitter) !== normalizeUrl(initialValues.twitter) ||
            normalizeUrl(currentValues.linkedin) !== normalizeUrl(initialValues.linkedin) ||
            currentValues.allCountry !== initialValues.allCountry ||
            (currentValues.workCoverageKind || 'specificZones') !== (initialValues.workCoverageKind || 'specificZones') ||
            (currentValues.influenceLat ?? null) !== (initialValues.influenceLat ?? null) ||
            (currentValues.influenceLng ?? null) !== (initialValues.influenceLng ?? null) ||
            Number(currentValues.influenceRadiusKm) !== Number(initialValues.influenceRadiusKm);

        const hasImageChanges = !!file || (!!imagePreview && imagePreview !== initialImageRef.current);

        const compareSkills = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return false;
            const getSkillId = (skill) => skill?.id || skill?.name || skill;
            const ids1 = arr1.map(getSkillId).sort();
            const ids2 = arr2.map(getSkillId).sort();
            return JSON.stringify(ids1) === JSON.stringify(ids2);
        };

        const hasSkillsChanges = !compareSkills(initialSkills, currentSkills);

        const compareZones = (zones1, zones2) => {
            if (zones1.length !== zones2.length) return false;
            if (zones1.length === 0 && zones2.length === 0) return true;
            
            const normalizeZone = (zone) => {
                const city = (zone.city || "").trim().toLowerCase();
                const name = (zone.name || zone.jurisdiccion?.name || "").trim().toLowerCase();
                const id = String(zone.jurisdiccion?.id || zone.jurisdiccion || "");
                return { city, name, id };
            };
            
            const sorted1 = zones1.map(normalizeZone).sort((a, b) => `${a.id}-${a.city}`.localeCompare(`${b.id}-${b.city}`));
            const sorted2 = zones2.map(normalizeZone).sort((a, b) => `${a.id}-${a.city}`.localeCompare(`${b.id}-${b.city}`));
            return JSON.stringify(sorted1) === JSON.stringify(sorted2);
        };

        const hasZonesChanges = !compareZones(
            initialValues.jobBoardZones || [],
            currentValues.jobBoardZones || []
        );

        const compareCategoryConnect = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return false;
            const sorted1 = [...arr1].sort();
            const sorted2 = [...arr2].sort();
            return JSON.stringify(sorted1) === JSON.stringify(sorted2);
        };

        const hasCategoryConnectChanges = !compareCategoryConnect(
            initialValues.categoryConnect || [],
            currentValues.categoryConnect || []
        );

        return hasFormChanges || hasImageChanges || hasSkillsChanges || hasZonesChanges || hasCategoryConnectChanges;
    }, [formik.values, formik.initialValues, userSkills, file, imagePreview]);

    const canSave = useMemo(() => {
        return formik.isValid &&
               userSkills?.length > 0 &&
               hasChanges &&
               isProfileJobBoardSaveReady(formik.values);
    }, [formik.isValid, formik.values, userSkills, hasChanges]);

    const handleSubmit = async () => {
        await updateUserProfileNew(formik.values, file, userSkills, formik.values.categoryConnect);

        setIsEditing(false);
    };

    return (
        <ModalOverlay isOpen={true} onClose={() => setIsEditing(false)}>
            <AnimatePresence>
                <motion.div
                    className={styles.modalContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={variants}
                    transition={{ duration: 0.3 }}
                >
                    <header className={styles.modalHeader}>
                        {isPhone ? (
                            !showLeftSide ? (
                                <div className={styles.modalHeader_mobile}>
                                    <button
                                        className={styles.modalHeader_backButton}
                                        onClick={handleBackToMenu}
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <h2 className={styles.modalHeader_title}>{activeSection}</h2>
                                </div>
                            ) : (
                                <h2 className={styles.modalHeader_title}>Editar Perfil</h2>
                            )
                        ) : (
                            <h2 className={styles.modalHeader_title}>Editar Perfil</h2>
                        )}
                        <button className={styles.modalHeader_close} onClick={() => setIsEditing(false)}><X size={20} /></button>
                    </header>
                    <div className={styles.modalContent}>
                    {(!isPhone || showLeftSide) && (
                        <div className={`${styles.modalContent_left} ${isPhone ? styles.phoneWidth : ''}`}>
                            <div className={styles.modalContent_left_data}>
                                <div className={styles.modalContent_left_data_image}>
                                    {imagePreview ? (
                                        <img
                                            className={styles.modalContent_left_data_image_img}
                                            src={imagePreview}
                                            alt="user"
                                        />
                                    ) : (
                                        !imageError && profileImage ? (
                                            <img
                                                className={styles.modalContent_left_data_image_img}
                                                src={profileImage}
                                                alt="user"
                                            />
                                        ) : (
                                            <div className={styles.modalContent_left_data_image_initials}>{initials}</div>
                                        ))
                                    }
                                    <div className={styles.modalContent_left_data_image_overlay} onClick={handleEditImgClick}>
                                        <Camera size={20} />
                                    </div>
                                    <label htmlFor="image" className={styles.labelHidden}>
                                        <input
                                            ref={inputFileRef}
                                            type="file"
                                            name="image"
                                            id="image"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    setImagePreview(URL.createObjectURL(file));
                                                    setFile(file);
                                                    formik.setFieldValue("image", file);
                                                }
                                            }}
                                            className={styles.userInfo_label}
                                            accept="image/*"
                                        />
                                    </label>
                                </div>
                                <h3 className={styles.modalContent_left_data_name}>{userFullName}</h3>
                                <p className={styles.modalContent_left_data_mail}>{userData?.username}</p>
                            </div>
                            <div className={styles.modalContent_left_nav}>
                                <button
                                    className={activeSection === 'Datos personales' ? styles.modalContent_left_nav_button_active : styles.modalContent_left_nav_button}
                                    onClick={() => handleSectionChange('Datos personales')}
                                >
                                    <UserRound size={20} />
                                    <span style={{marginRight: "auto"}}>Datos personales</span>
                                    {isPhone && <ChevronRight size={20} />}
                                </button>
                                <button
                                    className={activeSection === 'Zona de trabajo' ? styles.modalContent_left_nav_button_active : styles.modalContent_left_nav_button}
                                    onClick={() => handleSectionChange('Zona de trabajo')}
                                >
                                    <MapPin size={20} />
                                    <span style={{marginRight: "auto"}}>Zona de trabajo</span>
                                    {isPhone && <ChevronRight size={20} />}
                                </button>
                                <button
                                    className={activeSection === 'Mis rubros' ? styles.modalContent_left_nav_button_active : styles.modalContent_left_nav_button}
                                    onClick={() => handleSectionChange('Mis rubros')}
                                >
                                    <HardHat size={20} />
                                    <span style={{marginRight: "auto"}}>Mis rubros</span>
                                    {isPhone && <ChevronRight size={20} />}
                                </button>
                                <button
                                    className={activeSection === 'Conexiones' ? styles.modalContent_left_nav_button_active : styles.modalContent_left_nav_button}
                                    onClick={() => handleSectionChange('Conexiones')}
                                >
                                    <Users size={20} />
                                    <span style={{marginRight: "auto"}}>Conexiones</span>
                                    {isPhone && <ChevronRight size={20} />}
                                </button>
                                <button
                                    className={activeSection === 'Redes' ? styles.modalContent_left_nav_button_active : styles.modalContent_left_nav_button}
                                    onClick={() => handleSectionChange('Redes')}
                                >
                                    <LinkIcon size={20} />
                                    <span style={{marginRight: "auto"}}>Redes</span>
                                    {isPhone && <ChevronRight size={20} />}
                                </button>
                            </div>

                        </div>
                    )}
                    {(!isPhone || !showLeftSide) && (
                        <div className={styles.modalContent_right}>
                            {renderContent()}
                        </div>
                    )}
                    </div>
                    <div className={styles.modalFooter}>
                        {!formik.isValid && (
                            <div className={styles.toolTipError}>
                                <div className={styles.toolTipError_list}>
                                    <Info size={16} />
                                    Ver errores de validación
                                </div>
                                <ChevronDown size={16} onClick={() => setShowErrors(!showErrors)} className={styles.toolTipError_icon} />
                                {showErrors && (
                                    <div className={styles.toolTipError_errorMenu}>
                                        <ErrorMenu
                                            setShowErrors={setShowErrors}
                                            errors={formik.errors}
                                            handleSectionChange={handleSectionChange} />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={styles.modalFooter_buttonContainer}>
                            <button className={styles.modalFooter_btnClose} onClick={() => setIsEditing(false)}>Cancelar</button>
                            <button
                                disabled={!canSave}
                                className={styles.modalFooter_submit}
                                type="submit"
                                onClick={handleSubmit}
                            >
                                Guardar cambios
                            </button>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </ModalOverlay>
    );
};

const mapStateToProps = (state) => {
    return {
        userSkills: state.register.userSkills,
        cities: state.register.cities,
    };
}

export default connect(mapStateToProps, {
    getCities,
    getUserSkill,
    updateUserProfileNew
})(ProfileEditModalNew);